// src/pages/Organizations.js
import { useState, useEffect, useContext } from "react";
import { Typography } from "@mui/material";
import axios from "axios";
import { useTranslation } from "react-i18next";
import Modal from "../../components/Modal";
import CustomCheckbox from "../../components/CustomCheckbox";
import CustomButton from "../../components/CustomButton";
import CustomInput from "../../components/CustomInput";
import useLanguage from "../../utils/useLanguage";
import { useNavigate } from "react-router";
import ErrorBox from "../../components/ErrorBox";
import Dropdown from "../../components/Dropdown";
import BackToDashboard from "../../components/BackToDashboard";
import { DataContext } from "../../utils/DataContext";
import Loader from "../../components/Loader";
import { UserAuth } from "../../utils/AuthContext";
import PageWrapper from "../../components/PageWrapper";

export default function OrganizationsPage() {
  const { t } = useTranslation("general");
  const { currentDirection } = useLanguage();
  const [organizations, setOrganizations] = useState([]);
  const [filteredOrganizations, setFilteredOrganizations] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [organizationModal, setOrganizationModal] = useState(false);
  const [loadingModal, setLoadingModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [organizationFields, setOrganizationFields] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [searchNonce, setSearchNonce] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  const { loading, setLoading } = useContext(DataContext);
  const { userData } = useContext(UserAuth);

  const navigate = useNavigate();

  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const organizationTypes = [
    // { title: "pharmacy", value: "PHARMACY" },
    { title: "merchantOrg", value: "NO_CUSTOMER_MERCHANT" },
    { title: "branchedMerchantOrg", value: "MERCHANT" },
    { title: "fabricStore", value: "FABRICSTORE" },
    { title: "supermarket", value: "SUPERMARKET" },
    { title: "clinic", value: "CLINIC" },
    { title: "paperFactory", value: "PAPERFACTORY" },
    { title: "school", value: "SCHOOL" },
    { title: "bankOrg", value: "BANK" },
  ];

  const [organizationType, setOrganizationType] = useState({
    title: "",
    value: "",
  });

  useEffect(() => {
    const getAllOrganizations = async () => await getOrganizations();
    getAllOrganizations();
  }, [currentPage, searchTerm, searchNonce]);

  const handleSearch = () => {
    setCurrentPage(1);
    setSearchTerm(searchInput);
    // Force refetch even when the same query is submitted again.
    setSearchNonce((prev) => prev + 1);
  };

  const getOrganizations = async () => {
    const token = localStorage.getItem("SuperAdminToken");

    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_URL}/getOrganizations`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            page: currentPage,
            limit: 5,
            search: searchTerm,
          },
        },
      );

      if (response.data) {
        setOrganizations(response.data.organizations || response.data);
        setFilteredOrganizations(response.data.organizations || response.data);
        setTotalPages(response.data.totalPages);
        setTotalRecords(response.data.totalRecords);
      } else {
        console.error("Failed to get the organizations");
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error("Failed to get the organizations", error);
    }
  };

  const openOrganizationModal = (org) => {
    setSelectedOrganization(org);
    setOrganizationFields({
      organizationName: org.organizationName,
      email: org.email,
      phoneNumber: org.phoneNumber,
      isBlocked: org.isBlocked,
    });
    setOrganizationType(
      organizationTypes.find((type) => type.value === org.organizationType),
    );
    setIsEditing(false);
    setOrganizationModal(true);
  };

  const handleInputChange = (e, fieldName) => {
    const value = fieldName === "isBlocked" ? e.target.checked : e.target.value;
    setOrganizationFields({
      ...organizationFields,
      [fieldName]: value,
    });
  };

  const handleSaveOrganization = async () => {
    try {
      setLoadingModal(true);
      const token =
        localStorage.getItem("SuperAdminToken") ||
        localStorage.getItem("adminToken") ||
        localStorage.getItem("token");

      const response = await axios.put(
        `${process.env.REACT_APP_URL}/api/organization/${selectedOrganization.id}`,
        {
          id: selectedOrganization?.id,
          organizationName: organizationFields.organizationName,
          email: organizationFields.email,
          phoneNumber: organizationFields.phoneNumber,
          isBlocked: organizationFields.isBlocked,
          organizationType: organizationType.value,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.status === 200) {
        setOrganizationModal(false);
      } else {
        console.error("Failed to update organization:", response.data.message);
      }
      setLoadingModal(false);
    } catch (error) {
      console.error("Error updating organization:", error);
      setLoadingModal(false);
    } finally {
      await getOrganizations();
    }
  };

  const handleCancel = () => {
    setRemoveOrganizationModal(false);
    setOrganizationModal(false);
    setSelectedOrganization(null);
    setLoadingModal(false);
  };

  const openRemoveOrganizationModal = (org) => {
    setSelectedOrganization(org);
    setRemoveOrganizationModal(true);
  };

  const [showRemoveOrganizationModal, setRemoveOrganizationModal] =
    useState(false);
  const handleDeleteOrganization = async () => {
    const token = localStorage.getItem("SuperAdminToken");
    setLoadingModal(true);
    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_URL}/api/organization/${selectedOrganization.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.data.message === "Organization deleted successfully") {
        setRemoveOrganizationModal(false);
        setOrganizationModal(false);
      } else {
        console.error("Failed to delete organization:", response.data.message);
      }
    } catch (error) {
      console.error("Error deleting organization:", error);
    } finally {
      setLoadingModal(false);
      setOrganizationModal(false);
      await getOrganizations();
    }
  };

  const [addOrganizationModal, setAddOrganizationModal] = useState(false);
  const [newOrganizationFields, setNewOrganizationFields] = useState({
    organizationName: "",
    email: "",
    password: "",
    confirmPassword: "",
    phoneNumber: "",
    isBlocked: true,
  });

  const openAddOrganizationModal = () => {
    setAddOrganizationModal(true);
    setNewOrganizationFields({
      organizationName: "",
      email: "",
      password: "",
      confirmPassword: "",
      phoneNumber: "",
      isBlocked: true,
    });
  };

  const handleNewOrganizationInputChange = (e, fieldName) => {
    const value = fieldName === "isBlocked" ? e.target.checked : e.target.value;
    setNewOrganizationFields((prevFields) => ({
      ...prevFields,
      [fieldName]: value,
    }));
  };

  const handleCreateOrganization = async (e) => {
    e.preventDefault();

    try {
      // Determine the language based on currentDirection
      const language = currentDirection === "rtl" ? "ar" : "en";

      // Empty fields validation
      if (
        !newOrganizationFields.email ||
        !newOrganizationFields.organizationName ||
        !newOrganizationFields.password ||
        !newOrganizationFields.confirmPassword ||
        !newOrganizationFields.phoneNumber
      ) {
        setError(true);
        setErrorMessage(t("emptyInputFields"));
        return;
      }

      // Email validation regex
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (newOrganizationFields.email) {
        if (!emailPattern.test(newOrganizationFields.email)) {
          setError(true);
          setErrorMessage(t("invalidEmail"));
          return;
        }
      }

      // Password match validation
      if (
        newOrganizationFields.password !== newOrganizationFields.confirmPassword
      ) {
        setError(true);
        setErrorMessage("Passwords do not match");
        return;
      }

      setLoadingModal(true);
      const token = localStorage.getItem("SuperAdminToken");
      const response = await axios.post(
        `${process.env.REACT_APP_URL}/register`,
        {
          organizationType: organizationType.value,
          email: newOrganizationFields.email,
          organizationName: newOrganizationFields.organizationName,
          password: newOrganizationFields.password,
          phoneNumber: newOrganizationFields.phoneNumber,
          isBlocked: newOrganizationFields.isBlocked,
          language,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.status === 200) {
        setError(false);
        setErrorMessage("");
        setAddOrganizationModal(false);
      }
    } catch (error) {
      if (error.response && error.response.data && error.response.data.error) {
        const errorKey = error.response.data.error;
        switch (errorKey) {
          case "emailAlreadyInUse":
            setError(true);
            setErrorMessage(t("emailAlreadyInUse"));
            break;
          case "invalidPhoneNumber":
            setError(true);
            setErrorMessage(t("invalidPhoneNumber"));
            break;
          default:
            setError(true);
            setErrorMessage(t("unexpectedError"));
            break;
        }
      } else {
        console.error("Network or other error:", error.message);
        setError(true);
        setErrorMessage(t("networkError"));
      }
    } finally {
      setLoadingModal(false);
      await getOrganizations();
    }
  };

  const handleCancelAddOrganization = () => {
    setError(false);
    setErrorMessage("");
    setAddOrganizationModal(false);
  };

  return (
    <PageWrapper
      style={{ direction: currentDirection }}
      title={t("organizations")}

    >

        <div className="users-title-section">
          {!loading && userData.userType === "superadmin" ? (
            <CustomButton
              title={t("createOrganization")}
              variant="blank"
              icon="addP"
              iconSize={20}
              onClick={openAddOrganizationModal}
            />
          ) : null}
        </div>

        {loading ? (
          <Loader />
        ) : (
          <>
            <div className="o-row o-row--gap-size_300">
              <CustomInput
                name="organizationName"
                value={searchInput}
                placeholder={searchInput ? "" : t("search")}
                onChange={(e) => setSearchInput(e.target.value)}
                thick={true}
              />
              <CustomButton
                title={t("search")}
                onClick={handleSearch}
                icon={"search"}
                iconSize={20}
              />
            </div>
            {filteredOrganizations.length === 0 ? (
              <p>{t("noOrganizations")}</p>
            ) : (
              <>
                <ul
                  className="users-list"
                  style={{ direction: currentDirection }}
                >
                  {filteredOrganizations.map((org) => (
                    <li
                      className="user-list-item"
                      key={org.id}
                      onClick={() => openOrganizationModal(org)}
                      style={{ cursor: "pointer" }}
                    >
                      <p className="user-username">
                        {org.organizationName || "No Name Available"}
                      </p>
                      {org.email && (
                        <Typography variant="subtitle1" color="textSecondary">
                          {org.email}
                        </Typography>
                      )}
                    </li>
                  ))}
                </ul>

                <section className="pagination">
                  <div className="pagination-buttons">
                    <CustomButton
                      variant="blank"
                      title={t("previous")}
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={currentPage === 1}
                      icon={currentDirection === "ltr" ? "previous" : "next"}
                      iconSize={15}
                    />
                    <span className="pagination-location">
                      {t("page")}: {currentPage} - {totalPages}
                    </span>
                    <CustomButton
                      title={t("next")}
                      variant="blank"
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      icon={currentDirection === "ltr" ? "next" : "previous"}
                      iconSize={15}
                      reverseIcon
                    />
                  </div>

                  <div className="pagination-buttons">
                    <span className="pagination-location">
                      {t("totalNumber")}: {totalRecords}
                    </span>
                  </div>
                </section>
              </>
            )}
          </>
        )}

        {organizationModal && (
          <Modal
            title={isEditing ? t("editOrganization") : t("organization")}
            onClose={handleCancel}
            footer={
              <>
                {loadingModal ? <Loader /> : null}
                <section className="edit-user-actions">
                  {isEditing ? (
                    <>
                      <CustomButton
                        title={t("confirm")}
                        onClick={handleSaveOrganization}
                        icon="save"
                        iconSize={16}
                        disabled={loadingModal}
                      />
                      <CustomButton
                        title={t("cancel")}
                        onClick={handleCancel}
                        variant="blank"
                        icon="close"
                        iconSize={14}
                      />
                    </>
                  ) : (
                    <>
                      <CustomButton
                        variant="blank"
                        title={t("edit")}
                        onClick={() => setIsEditing(true)}
                        icon="edit"
                        iconSize={18}
                      />
                      <CustomButton
                        title={t("delete")}
                        variant="red"
                        icon="delete"
                        iconSize={18}
                        onClick={() =>
                          openRemoveOrganizationModal(selectedOrganization)
                        }
                      />
                    </>
                  )}
                </section>
              </>
            }
          >
            <div>
              <section>
                <div>
                  {isEditing ? (
                    <div className="edit-user-entity">
                      <span>{t("organizationType")}: </span>
                      <Dropdown
                        options={organizationTypes}
                        selected={organizationType}
                        setSelected={setOrganizationType}
                        flex
                      />
                    </div>
                  ) : (
                    <div className="view-user-entity">
                      <p>{t("organizationType")}:</p>
                      <p>
                        {t(
                          organizationTypes.find(
                            (type) =>
                              type.value ===
                              selectedOrganization?.organizationType,
                          )?.title || selectedOrganization?.organizationType,
                        )}
                      </p>
                    </div>
                  )}
                </div>
              </section>
              <section>
                <div>
                  {isEditing ? (
                    <div className="edit-user-entity">
                      <span>{t("organizationName")}: </span>
                      <CustomInput
                        name="organizationName"
                        value={organizationFields.organizationName || ""}
                        onChange={(e) =>
                          handleInputChange(e, "organizationName")
                        }
                      />
                    </div>
                  ) : (
                    <div className="view-user-entity">
                      <p>{t("organizationName")}:</p>
                      <p>{selectedOrganization?.organizationName}</p>
                    </div>
                  )}
                </div>
              </section>
              <section>
                <div>
                  {isEditing ? (
                    <div className="edit-user-entity">
                      <span>{t("email")}: </span>
                      <CustomInput
                        name="email"
                        value={organizationFields.email || ""}
                        onChange={(e) => handleInputChange(e, "email")}
                      />
                    </div>
                  ) : (
                    <div className="view-user-entity">
                      <p>{t("email")}:</p>
                      <p>{selectedOrganization?.email}</p>
                    </div>
                  )}
                </div>
              </section>
              <section>
                <div>
                  {isEditing ? (
                    <div className="edit-user-entity">
                      <span>{t("phoneNumber")}: </span>
                      <CustomInput
                        name="phoneNumber"
                        value={organizationFields.phoneNumber || ""}
                        onChange={(e) => handleInputChange(e, "phoneNumber")}
                      />
                    </div>
                  ) : (
                    <div className="view-user-entity">
                      <p>{t("phoneNumber")}:</p>
                      <p>{selectedOrganization?.phoneNumber}</p>
                    </div>
                  )}
                </div>
              </section>
              <section>
                {isEditing ? (
                  <div className="edit-user-entity">
                    <div className="organization-row-section">
                      <CustomCheckbox
                        checked={organizationFields?.isBlocked}
                        onChange={(e) => handleInputChange(e, "isBlocked")}
                        color="primary"
                      />
                      <label>{t("isBlocked")}</label>
                    </div>
                  </div>
                ) : (
                  <div className="view-user-entity">
                    <p>{t("theOrganizationIsBlocked")}:</p>
                    <p>
                      {selectedOrganization?.isBlocked ? t("yes") : t("no")}
                    </p>
                  </div>
                )}
              </section>
            </div>
          </Modal>
        )}

        {showRemoveOrganizationModal && (
          <Modal
            title={t("deleteOrganization")}
            onClose={() => setRemoveOrganizationModal(false)}
            footer={
              <>
                {loadingModal ? <Loader /> : null}
                <section className="delete-user-actions">
                  <CustomButton
                    onClick={handleDeleteOrganization}
                    title={t("confirm")}
                    icon="save"
                    iconSize={16}
                    disabled={loadingModal}
                  />
                  <CustomButton
                    title={t("cancel")}
                    onClick={() => setRemoveOrganizationModal(false)}
                    variant="blank"
                    icon="close"
                    iconSize={14}
                  />
                </section>
              </>
            }
          >
            <div className="delete-modal-body">
              <h3>{t("areYouSureYouWantToDeleteThisOrganization")}</h3>
              <p>
                {selectedOrganization?.organizationName ||
                  selectedOrganization?.email}
              </p>
            </div>
          </Modal>
        )}

        {/* Add Organization Modal */}
        {addOrganizationModal && (
          <Modal
            title={t("createOrganization")}
            onClose={handleCancelAddOrganization}
            footer={
              <>
                {loadingModal ? <Loader /> : null}
                <section className="edit-user-actions">
                  <CustomButton
                    title={t("confirm")}
                    onClick={handleCreateOrganization}
                    icon="save"
                    iconSize={16}
                    disabled={loadingModal}
                  />
                  <CustomButton
                    title={t("cancel")}
                    onClick={handleCancelAddOrganization}
                    variant="blank"
                    icon="close"
                    iconSize={14}
                  />
                </section>
              </>
            }
          >
            <form onSubmit={handleCreateOrganization}>
              <div>
                {error && (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <ErrorBox>{errorMessage}</ErrorBox>
                  </div>
                )}
                <div className="edit-user-entity">
                  <span>{t("organizationType")}</span>
                  <Dropdown
                    options={organizationTypes}
                    selected={organizationType}
                    setSelected={setOrganizationType}
                    flex
                  />
                </div>
                <div className="edit-user-entity">
                  <span>{t("organizationName")}: </span>
                  <CustomInput
                    name="organizationName"
                    value={newOrganizationFields.organizationName}
                    onChange={(e) =>
                      handleNewOrganizationInputChange(e, "organizationName")
                    }
                  />
                </div>
                <div className="edit-user-entity">
                  <span>{t("email")}: </span>
                  <CustomInput
                    name="email"
                    type="email"
                    value={newOrganizationFields.email}
                    onChange={(e) =>
                      handleNewOrganizationInputChange(e, "email")
                    }
                    required
                  />
                </div>
                <div className="edit-user-entity">
                  <span>{t("phoneNumber")}: </span>
                  <CustomInput
                    name="phoneNumber"
                    value={newOrganizationFields.phoneNumber}
                    onChange={(e) =>
                      handleNewOrganizationInputChange(e, "phoneNumber")
                    }
                  />
                </div>
                <div className="edit-user-entity">
                  <span>{t("password")}: </span>
                  <CustomInput
                    name="password"
                    type="password"
                    value={newOrganizationFields.password}
                    onChange={(e) =>
                      handleNewOrganizationInputChange(e, "password")
                    }
                    required
                  />
                </div>
                <div className="edit-user-entity">
                  <span>{t("confirmPassword")}: </span>
                  <CustomInput
                    name="confirmPassword"
                    type="password"
                    value={newOrganizationFields.confirmPassword}
                    onChange={(e) =>
                      handleNewOrganizationInputChange(e, "confirmPassword")
                    }
                    required
                  />
                </div>
                <div className="edit-user-entity">
                  <div className="user-row-section">
                    <CustomCheckbox
                      checked={newOrganizationFields.isBlocked}
                      onChange={(e) =>
                        handleNewOrganizationInputChange(e, "isBlocked")
                      }
                      color="primary"
                    />
                    <label>{t("isBlocked")}</label>
                  </div>
                </div>
              </div>
            </form>
          </Modal>
        )}
      {/*</div>*/}
    </PageWrapper>
  );
}
